import { auditList } from "@ir/contracts";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useDeferredValue, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/Button";
import { Pagination } from "@/components/ui/Pagination";
import { AuditTable } from "@/features/audit/AuditTable";
import type { AuditFilters } from "@/features/audit/auditFilters";
import { apiFetch, buildQuery } from "@/lib/api";
import { authClient } from "@/lib/authClient";

const PAGE_SIZE = 50;

export const Route = createFileRoute("/_auth/admin/auditoria")({
  async beforeLoad() {
    const { data } = await authClient.getSession();
    // No hace falta narrowing después: este beforeLoad no propaga contexto.
    if (data?.user.role !== "ir_admin") redirect({ to: "/insights", throw: true });
  },
  component: AuditPage,
});

function useAuditPage() {
  const [filters, setFilters] = useState<AuditFilters>({});
  const [page, setPage] = useState(1);
  // Difiere la consulta para no lanzar una petición por cada tecla.
  const deferred = useDeferredValue(filters);
  const query = useQuery({
    queryKey: ["audit", deferred, page],
    queryFn: () =>
      apiFetch(`/api/audit${buildQuery({ ...deferred, page, pageSize: PAGE_SIZE })}`, {
        schema: auditList,
      }),
    placeholderData: keepPreviousData,
  });
  const changeFilters = (next: AuditFilters) => {
    setFilters(next);
    setPage(1);
  };
  return { filters, changeFilters, page, setPage, query };
}

function AuditPage() {
  const { t } = useTranslation();
  const { filters, changeFilters, page, setPage, query } = useAuditPage();
  const { data, isPending } = query;
  const hasFilters = Object.keys(filters).length > 0;

  return (
    <>
      <PageHeader
        title={t("admin.auditTitle")}
        subtitle={t("admin.auditSubtitle")}
        actions={
          hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                changeFilters({});
              }}
            >
              Limpiar filtros
            </Button>
          )
        }
      />

      <div className="px-8 py-6">
        <div className="overflow-x-auto rounded-[3px] border border-line bg-card">
          {isPending && <p className="p-4 text-nota text-ink3">{t("common.loading")}</p>}

          {data && (
            <>
              <AuditTable
                entries={data.items}
                filters={filters}
                onFiltersChange={changeFilters}
              />
              {data.items.length === 0 && (
                <p className="p-4 text-center text-nota text-ink3">
                  Ningún registro coincide con los filtros.
                </p>
              )}
              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                total={data.total}
                onPageChange={setPage}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}
